/**
 * FeatureFlags 頁面
 * Feature Flags 管理
 */

import { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { get, post, patch, del } from '../../api/superAdminClient';
import useSWR from 'swr';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorDisplay } from '../../components/ErrorDisplay';
import { Modal } from '../../components/Modal';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { zhTW } from 'date-fns/locale';

/**
 * Feature Flag 列表回應類型
 */
interface FeatureFlagListResponse {
  success: boolean;
  data: Array<{
    id: string;
    featureKey: string;
    featureName: string;
    description: string | null;
    isGloballyEnabled: boolean;
    enabledTenantIds: string[];
    config: Record<string, unknown> | null;
    createdAt: string;
    updatedAt: string;
  }>;
}

/**
 * 建立/更新 Feature Flag Schema
 */
const featureFlagSchema = z.object({
  featureKey: z.string().min(1, 'Feature Key 為必填').max(100),
  featureName: z.string().min(1, 'Feature Name 為必填').max(255),
  description: z.string().optional(),
  isGloballyEnabled: z.boolean().default(false),
  enabledTenantIds: z.array(z.string().uuid()).default([]),
  config: z.record(z.string(), z.any()).optional(),
});

type FeatureFlagFormData = z.infer<typeof featureFlagSchema>;

/**
 * FeatureFlags 頁面
 */
export function FeatureFlags() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingFlag, setEditingFlag] = useState<
    FeatureFlagListResponse['data'][0] | null
  >(null);
  const [deletingFlag, setDeletingFlag] = useState<
    FeatureFlagListResponse['data'][0] | null
  >(null);
  const [viewingTenantsFlag, setViewingTenantsFlag] = useState<string | null>(
    null
  );

  const { data, error, isLoading, mutate } = useSWR<FeatureFlagListResponse>(
    '/api/super-admin/feature-flags',
    get as any
  );

  const handleCreate = () => {
    setEditingFlag(null);
    setIsCreateModalOpen(true);
  };

  const handleEdit = (flag: FeatureFlagListResponse['data'][0]) => {
    setEditingFlag(flag);
    setIsCreateModalOpen(true);
  };

  const handleDelete = (flag: FeatureFlagListResponse['data'][0]) => {
    setDeletingFlag(flag);
  };

  const handleViewTenants = async (featureKey: string) => {
    setViewingTenantsFlag(featureKey);
  };

  const handleToggleGlobal = async (
    flag: FeatureFlagListResponse['data'][0]
  ) => {
    try {
      await patch(`/api/super-admin/feature-flags/${flag.featureKey}`, {
        isGloballyEnabled: !flag.isGloballyEnabled,
      });
      toast.success(`已${!flag.isGloballyEnabled ? '啟用' : '停用'}全域功能`);
      mutate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '操作失敗');
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingFlag) return;

    try {
      await del(`/api/super-admin/feature-flags/${deletingFlag.featureKey}`);
      toast.success('Feature Flag 已刪除');
      setDeletingFlag(null);
      mutate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '刪除失敗');
    }
  };

  return (
    <Layout title="Feature Flags">
      <div className="space-y-4">
        {/* 操作列 */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            共 {data?.data?.length || 0} 個 Feature Flags
          </div>
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            + 新增 Feature Flag
          </button>
        </div>

        {/* Feature Flags 列表 */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <LoadingSpinner size="lg" />
            </div>
          ) : error ? (
            <div className="p-8">
              <ErrorDisplay error={error} onRetry={() => mutate()} />
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Feature Key
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Feature Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    全域啟用
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    啟用租戶數
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    更新時間
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data?.data?.map(flag => (
                  <tr key={flag.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {flag.featureKey}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div>
                        <div className="font-medium">{flag.featureName}</div>
                        {flag.description && (
                          <div className="text-gray-500 text-xs mt-1">
                            {flag.description}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleGlobal(flag)}
                        className={`
                          relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                          ${flag.isGloballyEnabled ? 'bg-primary' : 'bg-gray-200'}
                        `}
                      >
                        <span
                          className={`
                            inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                            ${flag.isGloballyEnabled ? 'translate-x-6' : 'translate-x-1'}
                          `}
                        />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={() => handleViewTenants(flag.featureKey)}
                        className="text-primary hover:text-primary/80 transition-colors"
                      >
                        {flag.enabledTenantIds.length} 個租戶
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(new Date(flag.updatedAt), 'yyyy-MM-dd HH:mm', {
                        locale: zhTW,
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleEdit(flag)}
                        className="text-primary hover:text-primary/80 transition-colors"
                      >
                        編輯
                      </button>
                      <button
                        onClick={() => handleDelete(flag)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                      >
                        刪除
                      </button>
                    </td>
                  </tr>
                )) || (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-4 text-center text-gray-500"
                    >
                      尚無 Feature Flags
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* 建立/編輯 Modal */}
        <FeatureFlagFormModal
          isOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false);
            setEditingFlag(null);
          }}
          flag={editingFlag}
          onSuccess={() => {
            mutate();
            setIsCreateModalOpen(false);
            setEditingFlag(null);
          }}
        />

        {/* 查看啟用租戶 Modal */}
        <FeatureFlagTenantsModal
          featureKey={viewingTenantsFlag}
          isOpen={viewingTenantsFlag !== null}
          onClose={() => setViewingTenantsFlag(null)}
        />

        {/* 刪除確認對話框 */}
        <ConfirmDialog
          isOpen={deletingFlag !== null}
          onClose={() => setDeletingFlag(null)}
          onConfirm={handleConfirmDelete}
          title="刪除 Feature Flag"
          message={`確定要刪除 Feature Flag "${deletingFlag?.featureName}" 嗎？此操作無法復原。`}
          confirmText="刪除"
          variant="danger"
        />
      </div>
    </Layout>
  );
}

/**
 * Feature Flag 表單 Modal
 */
interface FeatureFlagFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  flag: FeatureFlagListResponse['data'][0] | null;
  onSuccess: () => void;
}

function FeatureFlagFormModal({
  isOpen,
  onClose,
  flag,
  onSuccess,
}: FeatureFlagFormModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [enabledTenantIds, setEnabledTenantIds] = useState<string[]>([]);

  // 取得租戶列表（用於選擇）
  const { data: tenantsData } = useSWR<{
    success: boolean;
    data: {
      tenants: Array<{ id: string; name: string }>;
    };
  }>('/api/super-admin/tenants?page=1&limit=100', get as any);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FeatureFlagFormData>({
    resolver: zodResolver(featureFlagSchema),
    defaultValues: flag
      ? {
          featureKey: flag.featureKey,
          featureName: flag.featureName,
          description: flag.description || '',
          isGloballyEnabled: flag.isGloballyEnabled,
          enabledTenantIds: flag.enabledTenantIds,
          config: flag.config || {},
        }
      : {
          featureKey: '',
          featureName: '',
          description: '',
          isGloballyEnabled: false,
          enabledTenantIds: [],
          config: {},
        },
  });

  useEffect(() => {
    if (flag) {
      setEnabledTenantIds(flag.enabledTenantIds);
    } else {
      setEnabledTenantIds([]);
    }
  }, [flag]);

  const onSubmit = async (formData: FeatureFlagFormData) => {
    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        enabledTenantIds,
      };

      if (flag) {
        // 更新
        await patch(
          `/api/super-admin/feature-flags/${flag.featureKey}`,
          payload
        );
        toast.success('Feature Flag 更新成功');
      } else {
        // 建立
        await post('/api/super-admin/feature-flags', payload);
        toast.success('Feature Flag 建立成功');
      }
      reset();
      onSuccess();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '操作失敗');
    } finally {
      setIsSubmitting(false);
    }
  };

  const footer = (
    <>
      <button
        onClick={onClose}
        disabled={isSubmitting}
        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
      >
        取消
      </button>
      <button
        onClick={handleSubmit(onSubmit)}
        disabled={isSubmitting}
        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
      >
        {isSubmitting ? '處理中...' : flag ? '更新' : '建立'}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={flag ? '編輯 Feature Flag' : '新增 Feature Flag'}
      footer={footer}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Feature Key <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            {...register('featureKey')}
            disabled={!!flag} // 編輯時不可修改 key
            className={`
              w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent
              ${flag ? 'bg-gray-100 cursor-not-allowed' : ''}
              ${errors.featureKey ? 'border-red-500' : 'border-gray-300'}
            `}
            placeholder="例如: new_checkout_flow"
          />
          {errors.featureKey && (
            <p className="mt-1 text-sm text-red-600">
              {errors.featureKey.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Feature Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            {...register('featureName')}
            className={`
              w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent
              ${errors.featureName ? 'border-red-500' : 'border-gray-300'}
            `}
            placeholder="例如: 新結帳流程"
          />
          {errors.featureName && (
            <p className="mt-1 text-sm text-red-600">
              {errors.featureName.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            描述
          </label>
          <textarea
            {...register('description')}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Feature Flag 的描述說明"
          />
        </div>

        <div>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              {...register('isGloballyEnabled')}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm text-gray-700">全域啟用</span>
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            啟用的租戶
          </label>
          <div className="border border-gray-300 rounded-lg p-4 max-h-60 overflow-y-auto">
            {tenantsData?.data?.tenants?.map(tenant => (
              <label
                key={tenant.id}
                className="flex items-center space-x-2 py-1"
              >
                <input
                  type="checkbox"
                  checked={enabledTenantIds.includes(tenant.id)}
                  onChange={e => {
                    if (e.target.checked) {
                      setEnabledTenantIds([...enabledTenantIds, tenant.id]);
                    } else {
                      setEnabledTenantIds(
                        enabledTenantIds.filter(id => id !== tenant.id)
                      );
                    }
                  }}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-700">{tenant.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            設定 (JSON)
          </label>
          <textarea
            {...register('config')}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
            placeholder='{"key": "value"}'
            defaultValue={JSON.stringify(flag?.config || {}, null, 2)}
            onChange={e => {
              try {
                const value = e.target.value ? JSON.parse(e.target.value) : {};
                register('config').onChange({
                  target: { value },
                });
              } catch {
                // 忽略 JSON 解析錯誤
              }
            }}
          />
        </div>
      </form>
    </Modal>
  );
}

/**
 * Feature Flag 啟用租戶 Modal
 */
interface FeatureFlagTenantsModalProps {
  featureKey: string | null;
  isOpen: boolean;
  onClose: () => void;
}

function FeatureFlagTenantsModal({
  featureKey,
  isOpen,
  onClose,
}: FeatureFlagTenantsModalProps) {
  const { data, error, isLoading } = useSWR<{
    success: boolean;
    data: Array<{
      id: string;
      name: string;
      slug: string;
    }>;
  }>(
    featureKey ? `/api/super-admin/feature-flags/${featureKey}/tenants` : null,
    get as any
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="啟用的租戶" size="md">
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <ErrorDisplay error={error} />
      ) : (
        <div className="space-y-2">
          {data?.data && data.data.length > 0 ? (
            <ul className="space-y-2">
              {data.data.map(tenant => (
                <li
                  key={tenant.id}
                  className="px-4 py-2 bg-gray-50 rounded-lg flex items-center justify-between"
                >
                  <div>
                    <div className="font-medium text-gray-900">
                      {tenant.name}
                    </div>
                    <div className="text-sm text-gray-500">{tenant.slug}</div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-center text-gray-500 py-8">尚無啟用的租戶</p>
          )}
        </div>
      )}
    </Modal>
  );
}
